import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Sparkles } from "lucide-react";
import { Reveal, Overline } from "./Reveal";
import { computeLifePath, LIFE_PATH } from "../data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Calculator = ({ onBook }) => {
    const [name, setName] = useState("");
    const [dob, setDob] = useState("");
    const [contact, setContact] = useState("");
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name || !dob || !contact) return;
        setLoading(true);
        const lp = computeLifePath(dob);
        try {
            await axios.post(`${API}/leads`, { name, dob, contact, life_path: lp });
        } catch (err) {
            console.error(err);
        }
        setResult(lp);
        setLoading(false);
    };

    const meaning = result ? LIFE_PATH[result] : null;

    return (
        <section data-testid="calculator-section" className="py-28 lg:py-36 px-6 relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.08] pointer-events-none">
                <img src="https://images.unsplash.com/photo-1513346940221-6f673d962e97?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" alt="" className="w-full h-full object-cover" />
            </div>

            <div className="max-w-3xl mx-auto relative">
                <Reveal className="text-center">
                    <Overline>Free Mini Reading</Overline>
                    <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#F0EBE0]">
                        Curious what your <span className="italic text-[#C9A962]">numbers</span> reveal?
                    </h2>
                    <p className="mt-4 text-sm font-light text-[#A19D94]">Discover your Life Path Number — free, instant, and just the beginning.</p>
                </Reveal>

                <Reveal delay={0.15}>
                    <div className="mt-12 rounded-2xl border border-white/10 bg-[#0E1B33]/80 backdrop-blur-xl p-8 lg:p-10 shadow-[0_0_60px_rgba(201,169,98,0.08)]">
                        <AnimatePresence mode="wait">
                            {!result ? (
                                <motion.form
                                    key="form"
                                    data-testid="calculator-form"
                                    onSubmit={submit}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-5"
                                >
                                    <input
                                        data-testid="calc-name-input"
                                        type="text"
                                        required
                                        placeholder="Your Name"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-4 text-sm text-[#F0EBE0] placeholder-[#5C5A56] transition-shadow"
                                    />
                                    <input
                                        data-testid="calc-dob-input"
                                        type="date"
                                        required
                                        value={dob}
                                        onChange={(e) => setDob(e.target.value)}
                                        className="gold-ring-focus w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-4 text-sm text-[#F0EBE0] placeholder-[#5C5A56] [color-scheme:dark]"
                                    />
                                    <input
                                        data-testid="calc-contact-input"
                                        type="text"
                                        required
                                        placeholder="WhatsApp Number or Email"
                                        value={contact}
                                        onChange={(e) => setContact(e.target.value)}
                                        className="gold-ring-focus sm:col-span-2 w-full bg-[#081226] border border-white/10 rounded-xl px-5 py-4 text-sm text-[#F0EBE0] placeholder-[#5C5A56]"
                                    />
                                    <button
                                        data-testid="calc-reveal-btn"
                                        type="submit"
                                        disabled={loading}
                                        className="sm:col-span-2 px-8 py-4 rounded-full bg-[#C9A962] text-[#081226] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#dcc083] hover:shadow-[0_0_35px_rgba(201,169,98,0.45)] transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        <Sparkles size={16} /> {loading ? "Revealing…" : "Reveal My Number"}
                                    </button>
                                </motion.form>
                            ) : (
                                <motion.div
                                    key="result"
                                    data-testid="calc-result"
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="text-center py-4"
                                >
                                    <p className="text-xs uppercase tracking-[0.3em] text-[#A19D94]">Your Life Path Number</p>
                                    <motion.p
                                        initial={{ scale: 0.3, opacity: 0 }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        transition={{ type: "spring", stiffness: 120, damping: 12, delay: 0.15 }}
                                        className="font-mono-num text-8xl lg:text-9xl font-light text-[#C9A962] text-glow-gold my-6"
                                    >
                                        {result}
                                    </motion.p>
                                    <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                                        <p className="font-display text-2xl italic text-[#F0EBE0]">{meaning.title}</p>
                                        <p className="mt-3 text-sm font-light text-[#A19D94] max-w-md mx-auto leading-relaxed">{meaning.text}</p>
                                        <p className="mt-6 text-xs text-[#5C5A56]">This is only a glimpse. A complete reading reveals your full story.</p>
                                        <button
                                            data-testid="calc-book-btn"
                                            onClick={onBook}
                                            className="mt-6 px-8 py-4 rounded-full bg-[#C9A962] text-[#081226] text-sm font-semibold uppercase tracking-[0.15em] hover:bg-[#dcc083] hover:shadow-[0_0_35px_rgba(201,169,98,0.45)] transition-all"
                                        >
                                            Book a Complete Consultation
                                        </button>
                                    </motion.div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </Reveal>
            </div>
        </section>
    );
};
